import React, { useEffect, useMemo, useState } from "react";
import { SLOTS, STATUS_LABEL, STATUS_RANK } from "../lib/slots";
import { inclusiveDays } from "../lib/calendar";
import { fmtDateDE, fmtDateDEWeekday } from "../lib/finance";
import { PlanningIcon, PLANNING_ICON_OPTIONS } from "../lib/planningIcons";
import { ChevronDown, ChevronUp } from "lucide-react";

const PLANNING_ICON_LABEL = Object.fromEntries(
  PLANNING_ICON_OPTIONS.map((o) => [o.value, o.label]),
);

// Column order per Block-2 spec:
// Slot | Projekt | Datum | Meilenstein | Zusatzinfo | Status | Start | Ende | Dauer.
// `milestone_date` owns the asc/desc sort control for the milestone column
// pair; `milestone_text` hosts the sorting-mode selector but has no separate
// sort behaviour. `client` is the existing field previously shown below the
// project name — it now lives in its own "Zusatzinfo" column.
const COLUMNS = [
  { key: "slot",           label: "Slot",        sortable: true,  width: "70px" },
  { key: "name",           label: "Projekt",     sortable: true,  width: "minmax(200px, 1.3fr)" },
  { key: "milestone_date", label: "Datum",       sortable: true,  width: "138px" },
  { key: "milestone_text", label: "Meilenstein", sortable: false, width: "minmax(280px, 1.2fr)" },
  { key: "client",         label: "Zusatzinfo",  sortable: true,  width: "minmax(170px, 1fr)" },
  { key: "status",         label: "Status",      sortable: true,  width: "115px" },
  { key: "start_date",     label: "Start",       sortable: true,  width: "138px" },
  { key: "end_date",       label: "Ende",        sortable: true,  width: "138px" },
  { key: "duration",       label: "Dauer",       sortable: true,  width: "70px" },
];

const nameCollator = new Intl.Collator("de", { sensitivity: "base", numeric: true });

// Extract the first recognisable date (or year) from an arbitrary Zusatzinfo
// string, returning an ISO-like sortable key (YYYYMMDD). Returns null when no
// date can be recognised — those rows always sort to the bottom.
// Supported formats (case-insensitive, whole-word):
//   - DD.MM.YYYY
//   - YYYY-MM-DD
//   - year-only (4 digits) → treated as YYYY0000, sorts before any dated
//     entry of that year, still after undated (undated stays at the bottom).
// The displayed text is never modified; this is purely a sort key.
export function extractZusatzinfoSortKey(text) {
  if (!text || typeof text !== "string") return null;
  // Collect every recognised match with its position, then pick the earliest
  // by textual position ("first recognisable date in textual order").
  const candidates = [];
  for (const m of text.matchAll(/\b(\d{2})\.(\d{2})\.(\d{4})\b/g)) {
    candidates.push({ index: m.index, key: `${m[3]}${m[2]}${m[1]}` });
  }
  for (const m of text.matchAll(/\b(\d{4})-(\d{2})-(\d{2})\b/g)) {
    candidates.push({ index: m.index, key: `${m[1]}${m[2]}${m[3]}` });
  }
  for (const m of text.matchAll(/\b(\d{4})\b/g)) {
    // Skip four-digit chunks that are actually the year part of a DMY match
    // (they otherwise sort year-only ahead of the concrete date at the same
    // position + 6).
    const before = text.slice(Math.max(0, m.index - 6), m.index);
    if (/\d{2}\.\d{2}\.$/.test(before)) continue;
    // Skip four-digit chunks that are the ISO year part (YYYY-MM-DD).
    const after = text.slice(m.index + 4, m.index + 7);
    if (/^-\d{2}$/.test(after)) continue;
    candidates.push({ index: m.index, key: `${m[1]}0000` });
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.index - b.index);
  return candidates[0].key;
}

function compare(a, b, key) {
  if (key === "duration") {
    const da = inclusiveDays(a.start_date, a.end_date) ?? 0;
    const db = inclusiveDays(b.start_date, b.end_date) ?? 0;
    return da - db;
  }
  if (key === "slot") return a.slot - b.slot;
  if (key === "status") return (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99);
  if (key === "name") return nameCollator.compare(a.name ?? "", b.name ?? "");
  const av = a[key] ?? "";
  const bv = b[key] ?? "";
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
}

function buildVisibleEntries(p) {
  // Shared ordered list — main milestone (if any) first, then visible
  // planning notes chronologically. Every event lives in one shared row so
  // date and text/icon cannot drift apart when text wraps.
  const entries = [];
  if (p.milestone_date) {
    entries.push({ kind: "main", date: p.milestone_date, title: p.milestone_title || "" });
  }
  (Array.isArray(p.planning_notes) ? p.planning_notes : [])
    .filter((n) => n?.show_in_planner && n?.date)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .forEach((n) => {
      entries.push({
        kind: "note",
        date: n.date,
        title: n.title || "",
        icon: n.planner_icon || "production",
      });
    });
  return entries;
}

export default function ProjectListView({ projects, onSelectProject, restoreState, onStateChange }) {
  const [sortKey, setSortKey] = useState(restoreState?.sortKey || "milestone_date");
  const [sortDir, setSortDir] = useState(restoreState?.sortDir || "asc");
  const [milestoneSortMode, setMilestoneSortMode] = useState(restoreState?.milestoneSortMode || "all");
  // "compact"  = one row per project (main + notes stacked in Datum/Meilenstein)
  // "expanded" = one row per project + one extra row per visible planning note
  const [displayMode, setDisplayMode] = useState(restoreState?.displayMode || "expanded");
  useEffect(() => {
    onStateChange?.({ sortKey, sortDir, milestoneSortMode, displayMode });
  }, [sortKey, sortDir, milestoneSortMode, displayMode, onStateChange]);
  const selectProject = (p) => onSelectProject?.(p, { sortKey, sortDir, milestoneSortMode, displayMode });

  const todayIso = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  // Effective sort date per project id for the milestone column. Nulls sort last.
  const milestoneSortDates = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      let best = null;
      if (p.milestone_date && p.milestone_date >= todayIso) best = p.milestone_date;
      if (milestoneSortMode === "all" && Array.isArray(p.planning_notes)) {
        for (const n of p.planning_notes) {
          if (!n?.show_in_planner || !n?.date) continue;
          if (n.date < todayIso) continue;
          if (p.start_date && n.date < p.start_date) continue;
          if (p.end_date && n.date > p.end_date) continue;
          if (best == null || n.date < best) best = n.date;
        }
      }
      map.set(p.id, best);
    }
    return map;
  }, [projects, milestoneSortMode, todayIso]);

  // Effective Zusatzinfo sort key per project id. Nulls sort last.
  const clientSortKeys = useMemo(() => {
    const map = new Map();
    for (const p of projects) map.set(p.id, extractZusatzinfoSortKey(p.client));
    return map;
  }, [projects]);

  const sorted = useMemo(() => {
    const copy = [...projects];
    copy.sort((a, b) => {
      if (sortKey === "milestone_date") {
        const da = milestoneSortDates.get(a.id);
        const db = milestoneSortDates.get(b.id);
        if (da == null && db == null) return a.slot - b.slot;
        if (da == null) return 1;
        if (db == null) return -1;
        if (da === db) return a.slot - b.slot;
        return sortDir === "asc" ? (da < db ? -1 : 1) : (da < db ? 1 : -1);
      }
      if (sortKey === "client") {
        // Sort by extracted date value. Undated rows always at the bottom in
        // both directions. Stable tie-breaker: slot number.
        const ka = clientSortKeys.get(a.id);
        const kb = clientSortKeys.get(b.id);
        if (ka == null && kb == null) return a.slot - b.slot;
        if (ka == null) return 1;
        if (kb == null) return -1;
        if (ka === kb) return a.slot - b.slot;
        return sortDir === "asc" ? (ka < kb ? -1 : 1) : (ka < kb ? 1 : -1);
      }
      const r = compare(a, b, sortKey);
      return sortDir === "asc" ? r : -r;
    });
    return copy;
  }, [projects, sortKey, sortDir, milestoneSortDates, clientSortKeys]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const gridTemplate = COLUMNS.map((c) => c.width).join(" ");

  // Sonderfall: "Einzelzeilen" + Sortiermodus "Alle Meilensteine" + Sortierung
  // nach Datum → alle Haupt- und Nebenmeilenstein-Zeilen werden als flache
  // Liste behandelt und rein anhand ihres eigenen Datums global sortiert.
  // Projekt-Gruppierung entfällt bewusst in genau dieser Kombination.
  const useFlatMilestoneRows =
    displayMode === "expanded" && milestoneSortMode === "all" && sortKey === "milestone_date";

  const flatMilestoneRows = useMemo(() => {
    if (!useFlatMilestoneRows) return null;
    const rows = [];
    for (const p of projects) {
      const entries = buildVisibleEntries(p);
      for (let i = 0; i < entries.length; i++) {
        const e = entries[i];
        rows.push({
          project: p,
          kind: e.kind,
          entry: e,
          noteIdx: e.kind === "note" ? i - (entries[0]?.kind === "main" ? 1 : 0) : null,
        });
      }
    }
    rows.sort((a, b) => {
      const da = a.entry.date || "";
      const db = b.entry.date || "";
      // Zeilen ohne Datum immer unten, unabhängig von der Richtung.
      if (!da && !db) return nameCollator.compare(a.project.name ?? "", b.project.name ?? "");
      if (!da) return 1;
      if (!db) return -1;
      if (da !== db) return sortDir === "asc" ? (da < db ? -1 : 1) : (da < db ? 1 : -1);
      // Bei identischem Datum: Hauptmeilenstein vor Nebenmeilenstein.
      if (a.kind !== b.kind) return a.kind === "main" ? -1 : 1;
      // Danach stabil nach Projektname (deutsche Collation).
      return nameCollator.compare(a.project.name ?? "", b.project.name ?? "");
    });
    return rows;
  }, [useFlatMilestoneRows, projects, sortDir]);

  return (
    <div className="flex-1 overflow-auto bg-slate-50" data-testid="project-list-view">
      <div className="min-w-[1200px] p-6">
        {/* Toolbar */}
        <div className="flex items-center justify-end gap-2 mb-2">
          <div className="inline-flex items-center rounded-sm border border-slate-200 bg-white overflow-hidden">
            {[
              { value: "compact",  label: "Kompakt" },
              { value: "expanded", label: "Einzelzeilen" },
            ].map((m, i) => {
              const active = displayMode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setDisplayMode(m.value)}
                  className={`h-7 px-2.5 text-[10px] tracking-[0.14em] uppercase whitespace-nowrap transition-colors ${
                    i > 0 ? "border-l border-slate-200" : ""
                  } ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                  data-testid={`list-display-mode-${m.value}`}
                  title={m.value === "expanded" ? "Eine Zeile pro Meilenstein" : "Kompakte Darstellung"}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Header */}
        <div
          className="grid items-center px-3 py-2 bg-white border border-slate-200 rounded-t-md text-[10px] tracking-[0.18em] uppercase text-slate-500"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          {COLUMNS.map((c) => {
            const isMilestoneText = c.key === "milestone_text";
            return (
              <div
                key={c.key}
                className={`flex items-center gap-1.5 ${isMilestoneText ? "justify-between" : ""} ${sortKey === c.key ? "text-slate-900" : ""}`}
              >
                <button
                  type="button"
                  onClick={() => c.sortable && toggleSort(c.key)}
                  disabled={!c.sortable}
                  className={`text-left flex items-center gap-1 ${c.sortable ? "hover:text-slate-900 cursor-pointer" : "cursor-default"}`}
                  data-testid={`list-sort-${c.key}`}
                >
                  <span>{c.label}</span>
                  {c.sortable && sortKey === c.key && (
                    sortDir === "asc"
                      ? <ChevronUp className="w-3 h-3" />
                      : <ChevronDown className="w-3 h-3" />
                  )}
                </button>
                {isMilestoneText && (
                  <select
                    value={milestoneSortMode}
                    onChange={(e) => setMilestoneSortMode(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-5 px-1 text-[9px] tracking-[0.14em] uppercase bg-white border border-slate-200 rounded-sm text-slate-600 hover:text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-300 cursor-pointer"
                    data-testid="list-milestone-sort-mode"
                    title="Sortierung Meilenstein-Spalte"
                  >
                    <option value="main">Hauptmeilenstein</option>
                    <option value="all">Alle Meilensteine</option>
                  </select>
                )}
              </div>
            );
          })}
        </div>

        {/* Rows */}
        <div className="border-x border-b border-slate-200 rounded-b-md bg-white">
          {sorted.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-slate-400">
              Keine Projekte vorhanden.
            </div>
          ) : useFlatMilestoneRows ? (
            flatMilestoneRows.map((r, i) => (
              r.kind === "main" ? (
                <MainProjectRow
                  key={`main-${r.project.id}`}
                  project={r.project}
                  entry={r.entry}
                  gridTemplate={gridTemplate}
                  onSelectProject={selectProject}
                />
              ) : (
                <NoteRow
                  key={`note-${r.project.id}-${r.noteIdx ?? i}`}
                  project={r.project}
                  entry={r.entry}
                  gridTemplate={gridTemplate}
                  noteIdx={r.noteIdx ?? i}
                  onSelectProject={selectProject}
                />
              )
            ))
          ) : (
            sorted.map((p) => (
              <ProjectRows
                key={p.id}
                project={p}
                gridTemplate={gridTemplate}
                displayMode={displayMode}
                onSelectProject={selectProject}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectRows({ project, gridTemplate, displayMode, onSelectProject }) {
  const p = project;
  const slot = SLOTS[p.slot - 1];
  const days = inclusiveDays(p.start_date, p.end_date);
  const entries = buildVisibleEntries(p);
  const notes = entries.filter((e) => e.kind === "note");

  // Compact mode: one row per project. Datum + Meilenstein columns are
  // merged into a single col-span-2 sub-grid so every event row keeps its
  // date and text aligned even when the text wraps to multiple lines.
  if (displayMode === "compact") {
    return (
      <button
        onClick={() => onSelectProject?.(p)}
        className="w-full grid items-start px-3 py-2.5 text-left text-sm hover:bg-slate-50 border-t border-slate-100 first:border-t-0 transition-colors"
        style={{ gridTemplateColumns: gridTemplate }}
        data-testid={`list-row-${p.id}`}
      >
        <SlotCell slot={slot} n={p.slot} />
        <NameCell name={p.name} />
        <MilestoneGroupCell entries={entries} />
        <ClientCell client={p.client} />
        <StatusCell status={p.status} />
        <DateText value={p.start_date} />
        <DateText value={p.end_date} />
        <DateText raw={days ?? "—"} />
      </button>
    );
  }

  // Expanded (Einzelzeilen): main project row plus one extra row per visible
  // planning note. Each row is a real button so keyboard/click still opens
  // the same project. Notes render project name in italics.
  const mainEntry = entries.find((e) => e.kind === "main") || null;

  return (
    <React.Fragment>
      {mainEntry ? (
        <MainProjectRow
          project={p}
          entry={mainEntry}
          gridTemplate={gridTemplate}
          onSelectProject={onSelectProject}
        />
      ) : (
        <MainProjectRow
          project={p}
          entry={null}
          gridTemplate={gridTemplate}
          onSelectProject={onSelectProject}
        />
      )}
      {notes.map((n, i) => (
        <NoteRow
          key={`${p.id}-note-${i}`}
          project={p}
          entry={n}
          gridTemplate={gridTemplate}
          noteIdx={i}
          onSelectProject={onSelectProject}
        />
      ))}
    </React.Fragment>
  );
}

function MainProjectRow({ project, entry, gridTemplate, onSelectProject }) {
  const p = project;
  const slot = SLOTS[p.slot - 1];
  const days = inclusiveDays(p.start_date, p.end_date);
  return (
    <button
      onClick={() => onSelectProject?.(p)}
      className="w-full grid items-start px-3 py-2 text-left text-sm hover:bg-slate-50 border-t border-slate-100 first:border-t-0 transition-colors"
      style={{ gridTemplateColumns: gridTemplate }}
      data-testid={`list-row-${p.id}`}
    >
      <SlotCell slot={slot} n={p.slot} />
      <NameCell name={p.name} />
      {entry ? <MilestoneGroupCell entries={[entry]} /> : <EmptyMilestonePair />}
      <ClientCell client={p.client} />
      <StatusCell status={p.status} />
      <DateText value={p.start_date} />
      <DateText value={p.end_date} />
      <DateText raw={days ?? "—"} />
    </button>
  );
}

function NoteRow({ project, entry, gridTemplate, noteIdx, onSelectProject }) {
  const p = project;
  return (
    <button
      onClick={() => onSelectProject?.(p)}
      className="w-full grid items-start px-3 py-1.5 text-left text-sm hover:bg-slate-50 border-t border-dashed border-slate-100 bg-slate-50/40 transition-colors"
      style={{ gridTemplateColumns: gridTemplate }}
      data-testid={`list-row-${p.id}-note-${noteIdx}`}
    >
      <div />
      <div className="min-w-0">
        <div className="text-xs italic text-slate-500 truncate">{p.name}</div>
      </div>
      <MilestoneGroupCell entries={[entry]} />
      <div />
      <div />
      <div />
      <div />
      <div />
    </button>
  );
}

function SlotCell({ slot, n }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        className="w-2.5 h-2.5 rounded-sm border border-black/10 shrink-0"
        style={{ backgroundColor: slot?.color }}
      />
      <span className="tabular-nums text-slate-600">#{n}</span>
    </div>
  );
}

function NameCell({ name }) {
  return (
    <div className="min-w-0">
      <div className="font-medium text-slate-900 truncate">{name}</div>
    </div>
  );
}

function ClientCell({ client }) {
  return (
    <div className="min-w-0 text-xs text-slate-600 truncate" title={client || ""}>
      {client || <span className="text-slate-300">—</span>}
    </div>
  );
}

function StatusCell({ status }) {
  return <div className="text-xs text-slate-600">{STATUS_LABEL[status]}</div>;
}

function DateText({ value, raw }) {
  return (
    <div className="text-xs tabular-nums text-slate-600">
      {raw !== undefined ? raw : fmtDateDEWeekday(value)}
    </div>
  );
}

// Merges Datum + Meilenstein into one col-span-2 sub-grid. Each entry
// occupies one grid row (date on the left, text/icon on the right) so both
// stay pinned together even when the text wraps.
function MilestoneGroupCell({ entries }) {
  if (!entries || entries.length === 0) return <EmptyMilestonePair />;
  return (
    <div
      className="col-span-2 grid gap-y-0.5 min-w-0"
      style={{ gridTemplateColumns: "138px minmax(0, 1fr)" }}
    >
      {entries.map((e, i) => (
        <React.Fragment key={i}>
          <div
            className={`text-xs tabular-nums leading-5 pr-2 ${e.kind === "main" ? "text-slate-700" : "text-slate-500"}`}
          >
            {fmtDateDEWeekday(e.date)}
          </div>
          <div className="min-w-0">
            {e.kind === "main" ? (
              <div className="text-xs leading-5 text-slate-700 break-words">
                {e.title || <span className="text-slate-400">—</span>}
              </div>
            ) : (
              <div
                className="flex items-start gap-1.5 text-[11px] leading-5 text-slate-500 min-w-0"
                title={PLANNING_ICON_LABEL[e.icon] || "Planungs-Notiz"}
              >
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm bg-white border border-slate-200 shrink-0 mt-0.5">
                  <PlanningIcon icon={e.icon} size={10} color="#475569" />
                </span>
                <span className="break-words">{e.title || <span className="text-slate-400">—</span>}</span>
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function EmptyMilestonePair() {
  return (
    <div
      className="col-span-2 grid gap-y-0.5 min-w-0"
      style={{ gridTemplateColumns: "138px minmax(0, 1fr)" }}
    >
      <div className="text-xs tabular-nums text-slate-400">—</div>
      <div />
    </div>
  );
}
